import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

import { useColumnLabeledTextBackgroundData } from './hooks';

import { RichText } from 'components/Prismic';
import { Normalize } from 'utils/types';
import { filterProps } from 'utils/helpers';

export type TColumnLabeledTextBackgroundSection =
  Normalize<Queries.SolutionDesktopColumnLabeledTextBackgroundData_PrismicSolutionDataDesktopSolutionColumnLabeledTextBackground_Fragment>;

const Container = styled('div', filterProps('columns'))<{
  columns: number;
}>`
  ${({ theme: { colors }, columns }) => css`
    display: grid;
    position: relative;
    grid-template-columns: repeat(${columns}, calc(100% / ${columns}));
    align-content: flex-start;
    justify-content: center;
    margin-bottom: -1px;
    background-color: ${colors.black};
    overflow: hidden;
  `}
`;

const Content = styled.div`
  box-sizing: border-box;
  display: flex;
  position: absolute;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  z-index: 1;
  padding: 10rem 0 15.5rem;
  width: 100%;
  height: 100%;
`;

const Title = styled(RichText)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;

    & > h2 {
      text-align: center;
      line-height: 1.04;
      letter-spacing: -0.1em;
      color: ${colors.white};
      font-size: 12.25rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};
    }
  `}
`;

const Description = styled(RichText)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;
    background-color: #0d0c0ca6;
    backdrop-filter: blur(1rem);

    & > p {
      padding: 3.5rem 1.5rem;
      text-align: center;
      line-height: 1;
      color: ${colors.white};
      font-size: 3.25rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};
    }
  `}
`;

const ColumnImage = styled.div`
  display: flex;
  position: relative;
  height: 100%;
`;

const ColumnLabel = styled.span`
  ${({ theme: { colors, tp } }) => css`
    position: absolute;
    top: 105.375rem;
    background-color: ${colors.black}5C;
    padding: 5rem 0;
    width: 100%;
    text-align: center;
    line-height: 1.04;
    color: ${colors.white};
    font-size: 5rem;

    ${tp.useFont(fonts => fonts.Switzer.regular)};
  `}
`;

const ColumnLabeledTextBackgroundSection: React.FC<
  TColumnLabeledTextBackgroundSection
> = sliceData => {
  const { columnImages, title, description } = useColumnLabeledTextBackgroundData(sliceData);

  return (
    <Container columns={columnImages.length}>
      {columnImages.length
        ? columnImages.map(({ image, alt, label }, index) => (
            <ColumnImage key={`${alt}${index}`}>
              <GatsbyImage image={image} alt={alt} />
              <ColumnLabel>{label}</ColumnLabel>
            </ColumnImage>
          ))
        : null}
      <Content>
        <Title render={title} />
        <Description render={description} />
      </Content>
    </Container>
  );
};

export const desktopColumnLabeledTextBackgroundFragment = graphql`
  fragment SolutionDesktopColumnLabeledTextBackgroundData on PrismicSolutionDataDesktopSlicesType {
    ... on PrismicSolutionDataDesktopSolutionColumnLabeledTextBackground {
      slice_type

      primary {
        solution_column_labeled_text_background_title {
          richText
        }
        solution_column_labeled_text_background_description {
          richText
        }
      }

      items {
        solution_column_labeled_text_background_column_image {
          gatsbyImageData
          alt
        }
        solution_column_labeled_text_background_column_label
      }
    }
  }
`;

export default ColumnLabeledTextBackgroundSection;
